import React, { useState, useEffect } from 'react'
import { 
  Users, 
  Loader,
  AlertTriangle
} from 'lucide-react'
import { apiRequest } from '@/config/api'
import ApplicantFilters from './ApplicantFilters'
import ApplicantTableHeader from './ApplicantTableHeader'
import ApplicantTableRow from './ApplicantTableRow'

interface JobApplicant {
  id: number
  uuid: string
  application_id: number
  candidate_position_uuid?: string
  first_name: string
  last_name: string
  full_name: string
  email: string
  phone?: string
  current_title?: string
  current_company?: string
  current_location?: string
  total_experience_years?: number
  expected_salary?: number
  expected_salary_currency?: string
  skills: string[]
  linkedin_url?: string
  github_url?: string
  portfolio_url?: string
  resume_url?: string
  summary?: string
  source: string
  applicant_type: string
  application_status: string
  display_status: string
  applied_at: string
  last_updated: string
  position_title: string
  company_name: string
  resume_uuid?: string
  resume_filename?: string
  resume_file_path?: string
  resume_description?: string
  has_resume: boolean
}

interface PositionSkill {
  skill_id: number
  skill_name: string
  category: string
  proficiency_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  is_good_to_have?: boolean
}

interface PositionCertification {
  certification_name: string
  certification_body?: string
  is_required: boolean
  expiry_required?: boolean
}

interface TimelineEvent {
  type: string
  name: string
  date: string | null
  notes: string | null
  status: string
  byId: number | null
  byName: string | null
  metadata: any
}

interface JobApplicantsViewProps {
  positionUuid: string
  positionSkills?: PositionSkill[]
  positionCertifications?: PositionCertification[]
  onApplicantSelect?: (applicant: JobApplicant) => void
  onApplicantShortlisted?: () => void
  statusFilter?: string
}

export default function JobApplicantsView({ 
  positionUuid, 
  positionSkills = [], 
  positionCertifications = [], 
  onApplicantSelect, 
  onApplicantShortlisted, 
  statusFilter: propStatusFilter 
}: JobApplicantsViewProps) {
  const [applicants, setApplicants] = useState<JobApplicant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [expandedApplicant, setExpandedApplicant] = useState<number | null>(null)
  const [applicantTimelines, setApplicantTimelines] = useState<{[key: number]: TimelineEvent[]}>({})
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0
  })
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'score'>('score')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [loadingTimelines, setLoadingTimelines] = useState<Set<number>>(new Set())
  const [updating, setUpdating] = useState<number | null>(null)
  const [downloading, setDownloading] = useState<number | null>(null)

  const totalApplicants = (pagination.total && pagination.total > 0) ? pagination.total : applicants.length

  useEffect(() => {
    loadJobApplicants()
  }, [positionUuid, pagination.current_page, propStatusFilter])

  const toggleExpanded = async (applicantId: number) => {
    const isCurrentlyExpanded = expandedApplicant === applicantId
    
    if (isCurrentlyExpanded) {
      setExpandedApplicant(null)
    } else {
      setExpandedApplicant(applicantId)
      
      // Load timeline data if not already loaded
      if (!applicantTimelines[applicantId]) {
        await loadApplicantTimeline(applicantId)
      }
    }
  }

  const loadApplicantTimeline = async (applicantId: number) => {
    setLoadingTimelines(prev => new Set([...prev, applicantId]))
    
    try {
      // Use position-specific timeline endpoint with positionId parameter
      const timelineUrl = positionUuid 
        ? `candidates/${applicantId}/timeline?position_id=${positionUuid}`
        : `candidates/${applicantId}/timeline`
      
      const response = await apiRequest(timelineUrl)
      const data = await response.json()
      
      if (response.ok && data.success) {
        setApplicantTimelines(prev => ({
          ...prev,
          [applicantId]: data.data.timeline || []
        }))
      } else {
        console.error('Failed to load timeline:', data.error)
        // For now, use empty timeline
        setApplicantTimelines(prev => ({
          ...prev,
          [applicantId]: []
        }))
      }
    } catch (error) {
      console.error('Error loading timeline:', error)
      setApplicantTimelines(prev => ({
        ...prev,
        [applicantId]: []
      }))
    } finally {
      setLoadingTimelines(prev => {
        const newSet = new Set(prev)
        newSet.delete(applicantId)
        return newSet
      })
    }
  }

  const loadJobApplicants = async () => {
    try {
      setLoading(true)
      setError('')

      let url = `applicants/position/${positionUuid}/applicants?page=${pagination.current_page}&limit=${pagination.per_page}`
      if (propStatusFilter && propStatusFilter !== 'all') {
        url += `&status=${propStatusFilter}`
      }

      const response = await apiRequest(url)
      const data = await response.json()

      if (response.ok && data.success) {
        const fetchedApplicants = data.data.applicants || []
        const incomingPagination = data.data.pagination || {}

        const nextPagination = {
          current_page: incomingPagination.current_page || 1,
          per_page: incomingPagination.per_page || fetchedApplicants.length || pagination.per_page,
          total: incomingPagination.total && incomingPagination.total > 0
            ? incomingPagination.total
            : fetchedApplicants.length,
          total_pages: incomingPagination.total_pages
            || Math.max(1, Math.ceil((incomingPagination.total && incomingPagination.total > 0
              ? incomingPagination.total
              : fetchedApplicants.length) / (incomingPagination.per_page || fetchedApplicants.length || 1)))
        }

        setApplicants(fetchedApplicants)
        setPagination(nextPagination)
      } else {
        setError(data.error || 'Failed to load job applicants')
      }
    } catch (err: any) {
      setError('Failed to load job applicants')
      console.error('Error loading job applicants:', err)
    } finally {
      setLoading(false)
    }
  }

  const shortlistApplicant = async (applicationId: number, notes?: string) => {
    try {
      setUpdating(applicationId)
      setError('')

      const response = await apiRequest(`applicants/application/${applicationId}/shortlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          application_id: applicationId,
          notes: notes
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Update the local state immediately for better UX
        setApplicants(prev => prev.map(applicant => 
          applicant.application_id === applicationId 
            ? { 
                ...applicant, 
                application_status: 'shortlisted',
                display_status: 'shortlisted',
                last_updated: new Date().toISOString()
              }
            : applicant
        ))
        
        // Call the callback to refresh parent counts
        if (onApplicantShortlisted) {
          onApplicantShortlisted()
        }
        
        // Also refresh the full list to ensure consistency
        setTimeout(() => loadJobApplicants(), 500)
      } else {
        setError(data.error || data.message || 'Failed to shortlist applicant')
        console.error('Shortlist failed:', data)
      }
    } catch (err: any) {
      setError('Failed to shortlist applicant: ' + (err.message || 'Unknown error'))
      console.error('Error shortlisting applicant:', err)
    } finally {
      setUpdating(null)
    }
  }

  const updateApplicantStatus = async (applicationId: number, status: string, notes?: string) => {
    try {
      setUpdating(applicationId)
      setError('')

      const response = await apiRequest(`applicants/application/${applicationId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: status,
          notes: notes
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Update the local state immediately for better UX
        setApplicants(prev => prev.map(applicant => 
          applicant.application_id === applicationId 
            ? { 
                ...applicant, 
                application_status: status,
                display_status: status,
                last_updated: new Date().toISOString()
              }
            : applicant
        ))
        
        // Call the callback to refresh parent counts
        if (onApplicantShortlisted) {
          onApplicantShortlisted()
        }
        
        // Also refresh the full list to ensure consistency
        setTimeout(() => loadJobApplicants(), 500)
      } else {
        setError(data.error || data.message || `Failed to update applicant status to ${status}`)
        console.error('Status update failed:', data)
      }
    } catch (err: any) {
      setError(`Failed to update applicant status: ${err.message || 'Unknown error'}`)
      console.error('Error updating applicant status:', err)
    } finally {
      setUpdating(null)
    }
  }

  const downloadResume = async (applicationId: number, filename?: string) => {
    try {
      setDownloading(applicationId)
      setError('')

      const response = await apiRequest(`applicants/application/${applicationId}/resume/download`)
      const data = await response.json()

      if (response.ok && data.success) {
        // Use the download URL from the API response
        const downloadUrl = data.data.download_url
        const originalFilename = data.data.original_filename || filename || 'resume.pdf'
        
        // Create a temporary link and trigger download
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = originalFilename
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        const errorMessage = data.error || data.message || 'Failed to get resume download link'
        setError(errorMessage)
        console.error('Resume download failed:', data)
        
        // Show user-friendly alert for missing files
        if (errorMessage.includes('not found') || errorMessage.includes('NoSuchKey')) {
          alert('Resume file not available: The resume file may have been moved or deleted from the server.')
        }
      }
    } catch (err: any) {
      setError('Failed to download resume: ' + (err.message || 'Unknown error'))
      console.error('Error downloading resume:', err)
    } finally {
      setDownloading(null)
    }
  }

  const calculateSkillScore = (candidateSkills: string[], positionSkills: PositionSkill[]): number => {
    if (!candidateSkills || !positionSkills || positionSkills.length === 0) return 0
    
    let score = 0
    let maxScore = 0
    
    positionSkills.forEach(posSkill => {
      const points = posSkill.is_good_to_have ? 1 : 5
      maxScore += points
      
      const hasSkill = candidateSkills.some(candSkill => {
        const skillName = typeof candSkill === 'string' 
          ? candSkill.toLowerCase()
          : ((candSkill as any).skillName || (candSkill as any).skill_name || (candSkill as any).name || '').toLowerCase()
        return skillName === posSkill.skill_name.toLowerCase()
      })
      
      if (hasSkill) {
        score += points
      }
    })
    
    return maxScore > 0 ? Math.round((score / maxScore) * 100) : 0
  }

  const filteredApplicants = applicants
    .filter(applicant => {
      const matchesSearch = applicant.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           applicant.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           applicant.current_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           applicant.current_company?.toLowerCase().includes(searchTerm.toLowerCase())
      
      // If propStatusFilter is provided, it's already filtered by the API, so just search filter
      // If propStatusFilter is not provided, apply local status filtering
      const matchesStatus = !propStatusFilter ? (statusFilter === 'all' || applicant.display_status === statusFilter) : true

      return matchesSearch && matchesStatus
    })
    .sort((a, b) => {
      let aValue: any
      let bValue: any
      
      switch (sortBy) {
        case 'name':
          aValue = a.full_name.toLowerCase()
          bValue = b.full_name.toLowerCase()
          break
        case 'date':
          aValue = new Date(a.applied_at).getTime()
          bValue = new Date(b.applied_at).getTime()
          break
        case 'score':
          aValue = calculateSkillScore(a.skills, positionSkills)
          bValue = calculateSkillScore(b.skills, positionSkills)
          break
        default:
          return 0
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
    })

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading job applicants...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Users className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Job Applicants</h3>
              <p className="text-sm text-gray-600">External applicants who applied through job posting</p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {totalApplicants} total applicant{totalApplicants !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Filters */}
        <ApplicantFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          sortOrder={sortOrder}
          onSortOrderChange={setSortOrder}
          showStatusFilter={!propStatusFilter}
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="px-6 py-4 bg-red-50 border-l-4 border-red-400">
          <div className="flex">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <ApplicantTableHeader />
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredApplicants.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {searchTerm || statusFilter !== 'all' ? 'No applicants found' : 'No applicants yet'}
                  </h3>
                  <p className="text-gray-600">
                    {searchTerm || statusFilter !== 'all' 
                      ? 'Try adjusting your search or filter criteria.' 
                      : 'No external applicants have applied for this position yet.'}
                  </p>
                </td>
              </tr>
            ) : (
              filteredApplicants.map((applicant) => (
                <ApplicantTableRow
                  key={applicant.id}
                  applicant={applicant}
                  positionSkills={positionSkills}
                  positionCertifications={positionCertifications}
                  isExpanded={expandedApplicant === applicant.id}
                  isLoadingTimeline={loadingTimelines.has(applicant.id)}
                  timeline={applicantTimelines[applicant.id] || []}
                  updating={updating}
                  downloading={downloading}
                  onToggleExpanded={toggleExpanded}
                  onShortlist={shortlistApplicant}
                  onUpdateStatus={updateApplicantStatus}
                  onDownloadResume={downloadResume}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
              {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
              {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                disabled={pagination.current_page <= 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                disabled={pagination.current_page >= pagination.total_pages}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
